import { useParams, Navigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Star } from "lucide-react";
import { Layout } from "../components/Layout";
import { Seo, reviewSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { AffiliateButton, ScoreBadge } from "../components/Affiliate";
import { VpnLogo } from "../components/VpnLogo";
import { RichSections } from "../components/RichSections";
import { RelatedGuides } from "../components/RelatedGuides";
import { CtaBanner } from "../components/CtaBanner";
import { getVpn, VPNS } from "../data/vpns";
import { REVIEWS } from "../data/reviews";

const ReviewPage = () => {
  const { slug } = useParams();
  const vpn = getVpn(slug);
  const review = REVIEWS[slug];
  if (!vpn || !review) return <Navigate to="/reviews" replace />;

  return (
    <Layout>
      <Seo title={review.metaTitle} description={review.metaDescription} type="article" schema={reviewSchema(vpn, review)} />
      <section className="grain relative overflow-hidden bg-[#0F172A]">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">
            Review · Updated monthly {vpn.rank === 1 && "· Editor's Choice"}
          </p>
          <div className="flex flex-wrap items-center gap-8">
            <VpnLogo vpn={vpn} size={88} tile />
            <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              <MaskedLines lines={[review.h1]} />
            </h1>
            <ScoreBadge score={vpn.score} size="lg" />
          </div>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{vpn.tagline}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <AffiliateButton href={vpn.link} testId={`review-hero-cta-${vpn.slug}`}>
              {vpn.cta} from {vpn.price}/mo
            </AffiliateButton>
            <span className="text-xs text-slate-400">{vpn.details}</span>
          </div>
        </div>
      </section>

      <Breadcrumbs crumbs={[{ label: "Reviews", to: "/reviews" }, { label: `${vpn.name} Review` }]} />

      <div className="mx-auto max-w-4xl px-6 py-16 lg:px-8">
        <Reveal>
          <div className="mb-14 border-l-4 border-[#4687FF] bg-[#4687FF]/[0.05] p-8" data-testid="review-verdict-box">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#4687FF]">
              <Star size={14} fill="currentColor" /> {vpn.name} — Score: {vpn.score}/10
            </p>
            <p className="mt-3 text-base leading-relaxed text-slate-700">{review.verdictBox}</p>
          </div>
        </Reveal>

        <RichSections sections={review.sections.filter((s) => s.h !== "Pros & Cons")} />

        <Reveal className="my-14">
          <h2 className="mb-6 font-display text-2xl font-extrabold tracking-tight text-[#0F172A] sm:text-3xl">
            Pros & Cons
          </h2>
          <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2" data-testid="pros-cons-grid">
            <div className="bg-white p-8">
              <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-emerald-600">Pros</h3>
              <ul className="space-y-3">
                {vpn.pros.map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm leading-snug text-slate-700">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-500" /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8">
              <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-rose-600">Cons</h3>
              <ul className="space-y-3">
                {vpn.cons.map((c, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm leading-snug text-slate-700">
                    <XCircle size={16} className="mt-0.5 shrink-0 text-rose-400" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="border-t border-slate-200 pt-10">
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-400">
              Compare {vpn.name}
            </h3>
            <div className="mt-4 flex flex-wrap gap-3">
              {VPNS.filter((v) => v.slug !== vpn.slug).map((v) => {
                const pair = [vpn, v].sort((a, b) => a.rank - b.rank);
                return (
                  <Link
                    key={v.slug}
                    to={`/compare/${pair[0].slug}-vs-${pair[1].slug}`}
                    className="border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-[#4687FF] hover:text-[#4687FF]"
                  >
                    {vpn.name} vs {v.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </Reveal>

        <RelatedGuides vpn={vpn} />
      </div>
      <CtaBanner />
    </Layout>
  );
};

export default ReviewPage;
